import React from 'react';
import axios from 'axios';
import * as turf from '@turf/turf';
import _, { isEqual } from 'lodash';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { MAIN_MAP } from '../../../../redux/map/Constants';
import { withStyles } from '@material-ui/core/styles';
import { previewStyles } from '../../../Mapbox/Draw/draw-styles';
import Map from '../../PrintMapWithDraw';
import { SimplifyFilters } from '../../../../redux/layer_settings/Actions';

import { CircularProgress, Typography } from '@material-ui/core';
import PreviewMapImage from './PreviewMapImage';
import { MemoizedPrintMapInner } from './PrintMapInner';

function LoadingIndicator({ classes }) {
    return (
        <div className={classes.loadingInner}>
            <CircularProgress className={classes.loadingIcon} />
            <Typography color="textPrimary" variant="body1">
                Generating preview
            </Typography>
            <Typography color="textSecondary" variant="body2">
                This may take a while
            </Typography>
        </div>
    );
}

class PrintMap extends React.Component {
    constructor(props) {
        super(props);

        this.toPixels = this.toPixels.bind(this);
        this.toMeasure = this.toMeasure.bind(this);
        this.findMidpointFromHighlightedFeatures =
            this.findMidpointFromHighlightedFeatures.bind(this);
        this.findMidpointFromDrawnAreas = this.findMidpointFromDrawnAreas.bind(this);
        this.calculateCentre = this.calculateCentre.bind(this);
        this.mapSelectedSiteToDrawnFeature = this.mapSelectedSiteToDrawnFeature.bind(this);

        this.state = {
            image: null,
            mapCentre: null
        };

        this.map = null;
        this.isIdle = false;
    }

    componentDidMount() {
        this.calculateCentre();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.mapPosition !== this.props.mapPosition) {
            this.calculateCentre();
        }
    }

    // Converts from mm to pixels
    // Used to set the width and height of map HTML elements
    toPixels(length) {
        return (96 / 25.4) * length + 'px';
    }

    // Returns the destination point of the shortest distance great circle arc
    // Used to get the left, top, right and bottom bounds of the map as lnglat
    toMeasure(φ1, λ1, brng, d) {
        let R = 6371;
        φ1 *= Math.PI / 180;
        λ1 *= Math.PI / 180;
        brng *= Math.PI / 180;
        let φ2 = Math.asin(
            Math.sin(φ1) * Math.cos(d / R) + Math.cos(φ1) * Math.sin(d / R) * Math.cos(brng)
        );
        let λ2 =
            λ1 +
            Math.atan2(
                Math.sin(brng) * Math.sin(d / R) * Math.cos(φ1),
                Math.cos(d / R) - Math.sin(φ1) * Math.sin(φ2)
            );
        return [λ2 * (180 / Math.PI), φ2 * (180 / Math.PI)];
    }

    findMidpointFromHighlightedFeatures(features) {
        // TODO - Dirty hard coded hmlr layer, should be changed
        axios
            .get('/api/layers/159/geometry', {
                params: {
                    feature_ids: features.map(f => f.featureId)
                }
            })
            .then(res => {
                const centroid = turf.center(res.data);
                this.setState({
                    mapCentre: [centroid.geometry.coordinates[0], centroid.geometry.coordinates[1]]
                });
            })
            .catch(err => {
                this.selectedSite = null;
                console.error(err.response);
                const divider = features.length;
                const lngValue =
                    features.map(f => f.lngLat.lng).reduce((a, b) => a + b, 0) / divider;
                const latValue =
                    features.map(f => f.lngLat.lat).reduce((a, b) => a + b, 0) / divider;
                this.setState({ mapCentre: [lngValue, latValue] });
            });
    }

    findMidpointFromDrawnAreas(selectedFeatures) {
        const featureCollection = turf.featureCollection(selectedFeatures);
        const point = turf.center(featureCollection);
        return point.geometry.coordinates;
    }

    calculateCentre() {
        const { drawnFeatures, map, highlights, mapPosition } = this.props;
        switch (mapPosition) {
            case 'drawnArea':
                this.setState({
                    mapCentre: this.findMidpointFromDrawnAreas(drawnFeatures)
                });
                break;
            case 'selectedSite':
                // this.setState({
                //     mapCentre: null
                // }, () => { this.findMidpointFromHighlightedFeatures(highlights.highlightedFeatures.find(f => f.key.includes('_selected')).features) })
                this.findMidpointFromHighlightedFeatures(
                    highlights.highlightedFeatures.find(f => f.key.includes('_selected')).features
                );
                break;
            case 'mapView':
                if (map.mapPosition) {
                    const fitBounds = [
                        [map.mapPosition[0][0], map.mapPosition[0][1]],
                        [map.mapPosition[2][0], map.mapPosition[2][1]]
                    ];
                    this.setState({
                        mapCentre: [
                            (fitBounds[0][0] + fitBounds[1][0]) / 2,
                            (fitBounds[0][1] + fitBounds[1][1]) / 2
                        ]
                    });
                    break;
                }
            default:
                this.setState({
                    mapCentre: null
                });
                console.error('invalid map position');
        }
    }

    mapSelectedSiteToDrawnFeature(site) {
        return {
            geometry: {
                coordinates: [...site.polygons[0]]
            }
        };
    }

    handleIdle = () => {
        this.isIdle = true;
        const { onImageLoaded } = this.props;
        const image = this.map.getCanvas().toDataURL('image/png', 1.0);
        onImageLoaded(image, this.map.getBounds().toArray());
        this.setState({
            image: image
        });
    };

    handleDataLoading = () => {
        // Trigger only once when changing from idle to data loading
        if (this.isIdle) {
            this.isIdle = false;
            // REVIEW - This will throw up a warning which is annoying although the functionality appears to be safe
            // Just a side effect of mapbox event colliding with React
            this.setState({
                image: null
            });
            this.props.onImageLoaded(null, null);
        }
    };

    handleStyleLoaded = map => {
        this.map = map;
        map.on('idle', this.handleIdle);
        map.on('dataloading', this.handleDataLoading);
    };

    render() {
        const { classes } = this.props;
        const { image, mapCentre } = this.state;

        const { height, width, scale, includeDrawnFeatures, includeSiteCards } = this.props;
        const { baseLayer, activeLayers, layers, highlights, layerSettings, drawnFeatures } =
            this.props;

        if (mapCentre == null) {
            return <LoadingIndicator classes={classes} />;
        }

        const margin = 5;
        const content_width = 65;
        const content_left = width - margin - content_width;
        const map_width = content_left - margin - 2;
        const map_height = height - margin * 2;
        const mapAspect = map_width / map_height;

        const pageBounds = [
            [
                this.toMeasure(
                    mapCentre[1],
                    mapCentre[0],
                    270,
                    (map_width - 0.4) * 0.5 * 0.000001 * parseInt(scale)
                )[0],
                this.toMeasure(
                    mapCentre[1],
                    mapCentre[0],
                    180,
                    (map_height - 0.4) * 0.5 * 0.000001 * parseInt(scale)
                )[1]
            ],
            [
                this.toMeasure(
                    mapCentre[1],
                    mapCentre[0],
                    90,
                    (map_width - 0.4) * 0.5 * 0.000001 * parseInt(scale)
                )[0],
                this.toMeasure(
                    mapCentre[1],
                    mapCentre[0],
                    0,
                    (map_height - 0.4) * 0.5 * 0.000001 * parseInt(scale)
                )[1]
            ]
        ];

        // ToDo This should be moved out of this HOC as its functionality doesn't match the expected behaviour
        // This is responsible for combining an active layer with styling and filters and highlights for standalone map
        const processedActiveLayers = activeLayers
            .filter(l => !l.ignoreAttribution && l.printable)
            .map(el => {
                const layer = layers.find(e => e.system_name === el.systemName);
                const settings = layerSettings.find(setting => setting.uid === layer.system_name);
                if (!layer) {
                    console.error(`${el.systemName} does not exist for some reason`);
                }
                const style = layer.styles[(settings && settings.currentStyleIdx) || 0];
                return {
                    ...el,
                    layerData: {
                        ...layer.layerData,
                        filters:
                            layer.filterConfig && settings && settings.filters
                                ? SimplifyFilters(
                                      layer.filterConfig.fields,
                                      settings.filters.children
                                  )
                                : null
                    },
                    styleIdx: style.id,
                    styling: style.mapboxStyleSpecification,
                    primaryKey: layer.properties.id,
                    highlights: highlights.highlightedFeatures.filter(
                        e => e.layerSystemName === layer.system_name
                    )
                };
            });

        return (
            <div className={classes.root}>
                <div className={classes.mapRoot}>
                    {/* This acts an invisilbe large area to render the map to */}
                    <div
                        style={{
                            // height: this.toPixels(map_height - 0.4),
                            // width: this.toPixels(map_width - 0.4),
                            width: '1920px',
                            height: `${1920 / mapAspect}px`,
                            position: 'absolute'
                            // visibility: 'hidden'
                        }}>
                        <MemoizedPrintMapInner
                            baseLayer={baseLayer}
                            layers={layers}
                            activeLayers={processedActiveLayers}
                            drawnFeatures={
                                includeDrawnFeatures && drawnFeatures
                                    ? {
                                          type: 'FeatureCollection',
                                          features: _.cloneDeep(drawnFeatures)
                                      }
                                    : {
                                          type: 'FeatureCollection',
                                          features: []
                                      }
                            }
                            siteCardsEnabled={includeSiteCards}
                            fitBounds={[...pageBounds]}
                            onStyleLoaded={this.handleStyleLoaded}
                            // highlights={(formData.redLineBoundaryVisible) ?
                            //     [{
                            //         key: selectedHighlightInfo.layerSystemName,
                            //         layerSystemName: selectedHighlightInfo.layerSystemName,
                            //         features: selectedSites
                            //     }, {

                            //     }] : []
                            // }
                        />
                    </div>
                </div>
                {(!this.map || !this.map.loaded()) && <LoadingIndicator classes={classes} />}
                {this.map && this.map.loaded() && image && <PreviewMapImage mapImage={image} />}
            </div>
        );
    }
}

const styles = theme => ({
    root: {
        position: 'relative'
    },
    mapRoot: {
        width: '0px',
        height: '0px',
        overflow: 'hidden',
        position: 'relative'
    },
    loadingInner: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        // position: 'absolute',
        // top: 0,
        // left: 0,
        // bottom: 0,
        // right: 0
        '& > *:not(:first-child)': {
            paddingTop: 10
        }
    },
    loadingIcon: {
        '& > svg': {
            color: theme.palette.primary.light
        }
    }
});

const mapStateToProps = (state, ownProps) => ({
    // print: state.print_panel,
    layers: state.layers.layers,
    baseLayer: state.layers.baseLayers.find(bl => bl.system_name === ownProps.baseLayer),
    activeLayers: state.activeLayers,
    drawModule: state[MAIN_MAP].drawModule,
    drawnFeatures: state[MAIN_MAP].drawModule
        ? state[MAIN_MAP].drawModule.getDrawControl().getAll().features
        : [],
    map: state[MAIN_MAP],
    highlights: state.highlights,
    layerSettings: state.layerSettingsReducer
});

const mapDispatchToProps = dispatch => ({});

export default compose(connect(mapStateToProps, mapDispatchToProps), withStyles(styles))(PrintMap);
